import React, { useState, useEffect } from 'react';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { addSupportRequest, SupportRequest } from '../../../hooks/api-methods/useSupport';
import phone from '../../../assets/svg/phone.svg';
import email from '../../../assets/svg/email.svg';
import location from '../../../assets/svg/location.svg';
import ButtonSec from '../../../components/Buttons/ButtonSecundary';
import { useScreenSize } from '../../../hooks/useScreenSize';

import { AlertCircle } from 'lucide-react';
import Loader from '../../../components/Loader/Loader';
import SuccesModal from '../../../components/PopUps/SuccesModal';
import { getContactInfo } from '../../../hooks/api-methods/get-contact-info';

export interface ContactInfo {
  id: string;
  vat: string;
  email: string;
  phone: string;
  adress: string;
  mapsUrl?: string;
}

const Contact: React.FC = () => {
  const { isMobile, isTablet } = useScreenSize();
  const [formData, setFormData] = useState<SupportRequest>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    note: '',
    conditions: false,
  });
  const { errors, validateForm } = useFormValidation<SupportRequest>();
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [isError, setIsError] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm(formData);
    if (!isValid) return;

    setLoading(true);

    try {
      await addSupportRequest(formData);

      setModalTitle('Bericht verzonden');
      setModalMessage('Bedankt! We nemen zo snel mogelijk contact met je op.');
      setIsError(false);
      setModalOpen(true);

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        note: '',
        conditions: false,
      });
    } catch (error) {
      console.error(error);

      setModalTitle('Oeps…');
      setModalMessage('Er ging iets mis bij het verzenden. Probeer later opnieuw.');
      setIsError(true);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchContactInfo() {
      try {
        const data = await getContactInfo();
        setContactInfo(data);
        console.log(data);
      } catch (err) {
        console.error('Failed to load contact info', err);
      }
    }

    fetchContactInfo();
  }, []);

  const mapsHref = contactInfo?.mapsUrl
    ? contactInfo.mapsUrl
    : contactInfo?.adress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.adress)}`
      : undefined;

  return (
    <section className="slide slide-full flex-colomn gap-2" id="item6">
      {loading && <Loader />}

      <div className={`flex-row-align  ${isTablet ? 'flex-colomn' : 'flex-row gap-5'}`}>
        {/* LEFT SIDE */}
        <div className="flex-colomn justify-center w-100">
          <h2>
            <span className="tertiary-color">Contacteer </span>ons!
          </h2>

          <div className={`flex-colomn  ${isTablet ? 'gap-2' : 'gap-5'}`}>
            {/* hiddens on mobile */}
            <ul className="flex-colomn gap-2">
              {!isTablet && (
                <>
                  <li className="icon icon-small mt-025">
                    <li className="icon icon-small mt-025">
                      {mapsHref && (
                        <a
                          className="flex-row-align gap-2"
                          href={mapsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img src={location} alt="" />
                          <p>{contactInfo?.adress}</p>
                        </a>
                      )}
                    </li>
                  </li>
                  <li className="icon icon-small mt-025">
                    <a className="flex-row-align gap-2" href={`mailto:${contactInfo?.email}`}>
                      <img src={email} alt={'contact informatie airsolutions'} />
                      <p>{contactInfo?.email}</p>
                    </a>
                  </li>

                  <li className="icon icon-small mt-025">
                    <a className="flex-row-align gap-2" href={`tel:${contactInfo?.phone}`}>
                      <img src={phone} alt={'contact informatie airsolutions'} />
                      <p>{contactInfo?.phone}</p>
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* RIGHT SIDE — FORM */}
        <div className="flex-colomn gap-2 contact-form w-100">
          <form className="flex-colomn  w-100" onSubmit={handleSubmit}>
            <div className="relative">
              <input
                type="text"
                name="name"
                placeholder="Naam & Voornaam"
                className="rounded-input"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <small className="error-inline absolute flex-row-align ">
                  <AlertCircle size={14} />
                  {errors.name}
                </small>
              )}
            </div>
            <div className="relative">
              <input
                type="email"
                name="email"
                placeholder="E-mail"
                className="rounded-input"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <small className="error-inline absolute flex-row-align ">
                  <AlertCircle size={14} />
                  {errors.email}
                </small>
              )}
            </div>

            <div className="flex-row-align">
              <div className="relative w-100">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Telefoonnummer"
                  className="rounded-input"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <small className="error-inline absolute flex-row-align ">
                    <AlertCircle size={14} />
                    {errors.phone}
                  </small>
                )}
              </div>
              <div className="relative w-100">
                <select
                  name="subject"
                  className="rounded-input w-100"
                  value={formData.subject}
                  onChange={handleChange}
                >
                  <option value="">Kies een onderwerp</option>
                  <option value="advies">Advies</option>
                  <option value="offerte aanvragen">Offerte aanvragen</option>
                </select>
                {errors.subject && (
                  <small className="error-inline absolute flex-row-align ">
                    <AlertCircle size={14} />
                    {errors.subject}
                  </small>
                )}
              </div>
            </div>
            <div className="relative">
              <textarea
                name="note"
                placeholder="Bericht"
                className="rounded-input"
                rows={5}
                value={formData.note}
                onChange={handleChange}
              />
              {errors.note && (
                <small className="error-inline absolute flex-row-align ">
                  <AlertCircle size={14} />
                  {errors.note}
                </small>
              )}
            </div>

            {/* Privacy checkbox */}
            <div className={`${isMobile ? 'flex-colomn gap-2' : 'flex-row-align space-between'}`}>
              <label className="flex-row-align gap-1 checkbox-line relative">
                <input
                  type="checkbox"
                  name="conditions"
                  checked={formData.conditions}
                  onChange={handleChange}
                />
                <span>
                  Ik ga akkoord met de{' '}
                  <a href="/voorwaarden" className="privacy-link">
                    privacyvoorwaarden
                  </a>
                  .
                </span>
                {errors.conditions && (
                  <small className="error-inline flex-row-align ">
                    <AlertCircle size={14} />
                    {errors.conditions}
                  </small>
                )}
              </label>

              <ButtonSec text="Verstuur" type="submit" />
            </div>
          </form>
        </div>
      </div>
      <SuccesModal
        isOpen={modalOpen}
        title={modalTitle}
        message={modalMessage}
        onConfirm={() => setModalOpen(false)}
        confirmText={isError ? 'Sluiten' : 'Oké'}
      />
    </section>
  );
};

export default Contact;
